import { useRef, useState, type ReactNode } from "react";
import { useLingui } from "@lingui/react/macro";
import { ChevronDown, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { VirtualScroller } from "./VirtualScroller";
import type { PaginatedResponse } from "@/types/common";

export interface VirtualSelectFieldProps<TItem> {
  queryKeyBase: unknown[];
  buildQueryFn: (
    search: string,
  ) => (page: number, pageSize: number) => Promise<PaginatedResponse<TItem>>;
  pageSize?: number;

  getItemValue: (item: TItem) => string;
  getItemLabel: (item: TItem) => string;

  value: string;
  displayValue?: string;
  onChange: (value: string, item: TItem) => void;
  onClear?: () => void;

  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  /** Id for the trigger button, so a `<label htmlFor>` can point at this field. */
  triggerId?: string;
  triggerClassName?: string;
  emptyMessage?: string;
  renderSkeleton?: () => ReactNode;
  footer?: ReactNode;
}

export function VirtualSelectField<TItem>({
  queryKeyBase,
  buildQueryFn,
  pageSize = 20,
  getItemValue,
  getItemLabel,
  value,
  displayValue,
  onChange,
  onClear,
  placeholder,
  searchPlaceholder,
  disabled = false,
  triggerId,
  triggerClassName,
  emptyMessage,
  renderSkeleton,
  footer,
}: VirtualSelectFieldProps<TItem>) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // The dialog this select sits inside, if any. See `handleOpenChange`.
  const [container, setContainer] = useState<HTMLElement | null>(null);

  // Reset search each time the dropdown opens.
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setSearch("");
      // Radix portals popover content to `document.body`. Inside a modal
      // `Dialog` that puts the search box outside the dialog's focus trap, and
      // the trap pulls focus straight back to this trigger the moment
      // `onOpenAutoFocus` below hands it over — so the box looks focused, and
      // every keystroke lands on the trigger instead. The list then stays on its
      // unfiltered first page and the only way to reach an option is to scroll,
      // which for a paginated catalog means most options cannot be reached at
      // all. Portalling into the dialog puts both inside the same trap.
      setContainer(triggerRef.current?.closest<HTMLElement>('[role="dialog"]') ?? null);
    }
    setOpen(next);
  };

  const handleSelect = (item: TItem) => {
    onChange(getItemValue(item), item);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear?.();
  };

  const hasValue = !!value;
  // Never fall back to `value` — for every current consumer that is a raw
  // database id, and rendering it read as a value the user had chosen
  // ("Concept: 40"). Callers that hydrate a value are responsible for a
  // `displayValue`; `EntitySelect` resolves one automatically where it can.
  const label = displayValue;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          id={triggerId}
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-(--color-border) bg-(--color-surface-content) px-3 text-sm ring-offset-(--color-surface-page) focus:outline-none focus:ring-2 focus:ring-(--color-ring) focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            triggerClassName,
          )}
        >
          <span className={label ? "text-(--color-text-primary) truncate" : "text-(--color-text-secondary)"}>
            {label ?? placeholder ?? t`Select…`}
          </span>
          <span className="flex items-center gap-1 shrink-0 ml-2">
            {hasValue && onClear && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => e.key === "Enter" && handleClear(e as unknown as React.MouseEvent)}
                className="rounded p-0.5 text-(--color-text-secondary) hover:text-(--color-text-primary)"
              >
                <X className="size-3.5" />
              </span>
            )}
            <ChevronDown className={`size-4 text-(--color-text-secondary) transition-transform ${open ? "rotate-180" : ""}`} />
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        container={container}
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
        className="z-[60] w-(--radix-popover-trigger-width) p-0 overflow-hidden"
      >
        <div className="flex items-center gap-2 border-b border-(--color-border) px-3 py-2">
          <Search className="size-4 shrink-0 text-(--color-text-secondary)" />
          <Input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder ?? t`Search…`}
            className="h-7 border-none shadow-none focus-visible:ring-0 p-0 text-sm"
          />
        </div>

        <div className="max-h-60 overflow-y-auto">
          <VirtualScroller<TItem>
            queryKey={[...queryKeyBase, search]}
            queryFn={buildQueryFn(search)}
            pageSize={pageSize}
            enabled={open}
            staleTime={30_000}
            emptyMessage={emptyMessage}
            loadingRows={4}
            renderSkeleton={renderSkeleton}
            renderItem={(item) => {
              const itemValue = getItemValue(item);
              const isSelected = itemValue === value;
              return (
                <button
                  key={itemValue}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-(--color-surface-muted) transition-colors ${
                    isSelected ? "bg-(--color-surface-muted) font-medium" : ""
                  }`}
                >
                  {getItemLabel(item)}
                </button>
              );
            }}
          />
        </div>
        {footer && (
          <div className="border-t border-(--color-border)">
            {footer}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
